import type { AnyAction, Reducer } from 'redux';
import type { ActionCreator } from 'typescript-fsa';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';

export interface PaginationState {
  order: 'asc' | 'desc';
  sort?: string;
  offset: number;
  limit: number;
  page: number;
  maybeHasMore: boolean;
}

export function paginationFactory(overwrites: Partial<PaginationState> = {}): PaginationState {
  return {
    order: 'asc',
    offset: 0,
    limit: 100,
    maybeHasMore: false,
    page: 1,
    ...overwrites,
  };
}

export function getMaybeHasMoreStatus(state: PaginationState, resultLength: number) {
  return state.limit === resultLength;
}

interface PaginationSetParams extends Partial<PaginationState> {}

interface PaginationSetHasMore {
  maybeHasMore: boolean;
}

export interface PaginationModule {
  withPaginationReducer: <S extends { pagination: PaginationState }>(
    reducer: Reducer<S, AnyAction>
  ) => Reducer<S, AnyAction>;
  paginationActions: {
    setParams: ActionCreator<PaginationSetParams>;
    setMaybeHasMore: ActionCreator<PaginationSetHasMore>;
  };
}

export function createPaginationModule(prefix: string): PaginationModule {
  const actionCreator = actionCreatorFactory(`timely/${prefix}`);

  const setParamsAction = actionCreator<PaginationSetParams>('pagination/SET_PARAMS');
  const setMaybeHasMoreAction = actionCreator<PaginationSetHasMore>('pagination/SET_HAS_MORE');

  const withPaginationReducer = <S extends { pagination: PaginationState }>(
    parentReducer: Reducer<S, AnyAction>
  ): Reducer<S, AnyAction> => {
    const initialState = parentReducer(undefined, { type: '__pagination_initial_state' });

    const mapPagination =
      <T>(mapper: (subState: PaginationState, action: T) => Partial<PaginationState>) =>
      (state: S, action: T): S => {
        return {
          ...state,
          pagination: {
            ...state.pagination,
            ...mapper(state.pagination, action),
          },
        };
      };

    return reducerWithInitialState(initialState)
      .caseWithAction(
        setParamsAction,
        mapPagination((state, action) => {
          return {
            ...state,
            ...action.payload,
          };
        })
      )
      .caseWithAction(
        setMaybeHasMoreAction,
        mapPagination((state, action) => {
          return {
            ...state,
            maybeHasMore: action.payload.maybeHasMore,
          };
        })
      )
      .default(parentReducer);
  };

  return {
    withPaginationReducer,
    paginationActions: {
      setParams: setParamsAction,
      setMaybeHasMore: setMaybeHasMoreAction,
    },
  };
}
